import React from 'react';
import { useIntl } from 'react-intl';
import settings from 'configurations/application';
import authentication from 'services/authentication';
import location from 'services/router/location';
import application from 'services/application';
import { persist } from 'services/google-analytics/init';
import useNavigation from 'enhancers/use-navigation';
import withPortalRedirection from 'enhancers/with-portal-redirection';
import Html from 'components/html';
import Link from 'components/link/link';
import Alert from 'components/alert';
import Seo from 'pages/shared/seo';
import Loading from 'pages/shared/loading';
import AppHeaderControls from 'pages/shared/app-header-controls';

import Form from './form';
import messages from './login.i18n';
import styles from './login.module.scss';

const Login: React.FunctionComponent<unknown> = () => {
  const { formatMessage } = useIntl();
  const [navigation, { redirect }, link] = useNavigation();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, toggleError] = React.useState<'authentication' | 'generic'>();

  const redirectUser = React.useCallback((): void => {
    persist({ authenticated: authentication.isAuthenticated() });

    if (redirect) {
      return location.navigate(decodeURIComponent(redirect), '_self', true);
    }

    navigation[settings.homepage](undefined, 'replace');
  }, [navigation, redirect]);

  React.useEffect(() => {
    if (authentication.isAuthenticated()) {
      return redirectUser();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAuthentication = (username: string, password: string): void => {
    setLoading(true);
    toggleError(undefined);

    void authentication
      .signIn(username, password)
      .then(redirectUser)
      .catch(({ error: err }) => {
        toggleError(err === 'invalid_credentials' ? 'authentication' : 'generic');
      })
      .finally(() => setLoading(false));
  };

  const currentProduct = application.current;

  return (
    <React.Fragment>
      <Seo
        title={formatMessage(messages.pages.login.seo[currentProduct].title)}
        description={formatMessage(messages.pages.login.seo[currentProduct].title)}
      />
      <AppHeaderControls signup />
      <Html.div className="row h-100">
        <Html.div
          className={[
            'col-12',
            'col-lg-8',
            'offset-lg-2',
            'col-xl-8',
            'offset-xl-2',
            'col-xxl-6',
            'offset-xxl-3',
            styles.login,
          ]}
        >
          <Html.h1 typography="body1" className="m-0 text-center mb-10" fontWeight="bold">
            {formatMessage(messages.pages.login.header[currentProduct].title)}
          </Html.h1>
          {error && <Alert>{formatMessage(messages.pages.login.error[error])}</Alert>}
          <Form onAuthRequest={handleAuthentication} loading={loading} error={Boolean(error)} />
          <Html.div className={['col-12 mt-10 text-center']}>
            {formatMessage(messages.pages.login.footer[currentProduct].cta)}
            <Html.br />
            <Link href={link.signup()} variant="primary">
              {formatMessage(messages.pages.login.footer[currentProduct].createAccount)}
            </Link>
          </Html.div>
          <Html.div className={['col-12 mt-10 text-center']}>
            {formatMessage(messages.pages.login.header[currentProduct].support, {
              a: (text: React.ReactNode): React.ReactElement => (
                <Link href={link.onlineSupportChat({ topic: 'other' })} variant="primary">
                  {text}
                </Link>
              ),
            })}
          </Html.div>
        </Html.div>
      </Html.div>
    </React.Fragment>
  );
};

export default withPortalRedirection(Login, Loading, {
  ignoreQAModeFlag: true,
});
